import { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Native select styled to match Input component.
 *
 * @param {Object} props
 * @param {string} [props.label] - Select label
 * @param {string} [props.helperText] - Helper text below select
 * @param {string} [props.error] - Error message (also sets error styling)
 * @param {string} [props.placeholder] - Placeholder option text
 * @param {Array<{value: string, label: string}>} [props.options] - Select options
 * @param {string} [props.className] - Additional classes for wrapper
 * @param {string} [props.selectClassName] - Additional classes for select element
 */
export const Select = forwardRef(function Select(
  {
    label,
    helperText,
    error,
    placeholder,
    options = [],
    className,
    selectClassName,
    id,
    children,
    ...props
  },
  ref
) {
  const selectId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  const baseSelectStyles = `
    w-full px-3 py-2 pr-10 text-sm text-neutral-900 bg-white
    border rounded-md appearance-none cursor-pointer
    focus:outline-none focus:ring-2 focus:border-transparent
    disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-50
    transition-colors duration-150
  `;

  const selectStateStyles = error
    ? 'border-error-500 focus:ring-error-500'
    : 'border-neutral-300 focus:ring-primary-500';

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-neutral-700"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          className={cn(
            baseSelectStyles,
            selectStateStyles,
            selectClassName
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children || options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-neutral-500">
          <ChevronDown className="h-4 w-4" />
        </div>
      </div>
      {(helperText || error) && (
        <p className={cn(
          'text-sm',
          error ? 'text-error-600' : 'text-neutral-500'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});
